import React, {Component} from 'react';
import {ReactiveList} from '@appbaseio/reactivesearch';
import DOMPurify from 'dompurify';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import ResultItem, {resultCardHeader, resultListContainer} from '../../styles/ResultItem';
import Flex, {FlexChild} from '../../styles/Flex';
import Link from '../../styles/Link';
import Button from '../../styles/Button';
import theme from '../../styles/theme';
import {css} from 'react-emotion';
import empty from '../../assets/empty.png';
import {Logo, ModelLabel} from "./headerItems";
import {AccessSearch, GridSearch, ModelSearch, ParameterSearch} from "./Searches";
import {Status} from "./Status";
import {Enabled} from "./Enabled";
import {convertMinsToHrsMins, convertMinuteOfUtcDayToTime} from "../../utils/dateTime";

const onResultStats = (results, time) => {
    // Handle both ES 6.x (number) and ES 7.x (object) formats
    const totalResults = typeof results === 'object' && results.value !== undefined
        ? results.value
        : results;

    return (
        <Flex justifyContent="flex-end">
            {totalResults} results found in {time}ms
        </Flex>
    );
};

const tryParseJSON  = (jsonString) => {
    try {
        const o = JSON.parse(jsonString);
        if (o && typeof o === "object") {
            return o;
        }
    }
    catch (e) {
    }
    return false;
};

const hyphenate = (str) => {
    const replacedString = str.replace(/_/g, "&shy;_");
    const sanitizedReplacedString = DOMPurify.sanitize(replacedString)
    return (
        <span dangerouslySetInnerHTML={{__html: sanitizedReplacedString}}></span>
    )
};

const NoResult = () => {
    const center = css`
        display: block;
        margin-left: auto;
        margin-right: auto;
    `;

    return <img src={empty} className={center} alt={"Wow such empty"}/>
};

const UpdateFrequencyList = (data) => {
    const upFreq = data.updateFrequency;
    const listItems = Object.entries(upFreq.updates)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(up => up[1])
        .map(
            (update, index) => {
                if (update === null) {
                    return "";
                }
                return <li key={index}>{
                    update.updateTime
                    + ": "
                    +  update.timeRange.map(
                        range => ""
                            + convertMinsToHrsMins(Math.min(...range.timeSteps))
                            + " - "
                            + convertMinsToHrsMins(Math.max(...range.timeSteps))
                            + " / "
                            + ("minuteOfUtcDay" in range
                                ? convertMinuteOfUtcDayToTime(range.minuteOfUtcDay)
                                : convertMinsToHrsMins(range.resolution) + "h")
                        ).join(" + ")
                }</li>
            }
        );
    return <ul style={{marginTop: "0px", paddingLeft: "15px"}}>{listItems}</ul>;
};


class Results extends Component {
    constructor(props) {
        super(props);
        this.props = props;
        this.modelSearch = new ModelSearch({onError: this.onError, ...props});
        this.parameterSearch = new ParameterSearch({onError: this.onError, ...props});
        this.gridSearch = new GridSearch({onError: this.onError, ...props});
        this.accessSearch = new AccessSearch({onError: this.onError, ...props});
        this.resultItemId = 0;
    }

    onData = (data) => {
        // console.log(JSON.stringify(data, null, 2));
        return (
            <ResultItem key={this.resultItemId++}>
            <Flex className={resultCardHeader}>
                <FlexChild>
                    <Link href={data.url} target="_blank" rel="noopener noreferrer">
                        {hyphenate(data.name)}
                    </Link>
                    {data.package &&
                        <p style={{backgroundColor:'#F0E68C', width:'95%'}}><svg className="svg-inline--fa fa-square-info" aria-hidden="true" focusable="false"
                                data-prefix="fal" data-icon="square-info" role="img"
                                xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" data-fa-i2svg="">
                            <path fill="currentColor"
                                  d="M64 64C46.3 64 32 78.3 32 96V416c0 17.7 14.3 32 32 32H384c17.7 0 32-14.3 32-32V96c0-17.7-14.3-32-32-32H64zM0 96C0 60.7 28.7 32 64 32H384c35.3 0 64 28.7 64 64V416c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V96zm200 64a24 24 0 1 1 48 0 24 24 0 1 1 -48 0zm-16 64h40c8.8 0 16 7.2 16 16V352h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H176c-8.8 0-16-7.2-16-16s7.2-16 16-16h32V256H184c-8.8 0-16-7.2-16-16s7.2-16 16-16z"></path>
                        </svg> {data.packageElements}</p>
                    }
                </FlexChild>
                <FlexChild>
                    <Flex direction={"column"} justifyContent={"flex-end"}>
                        <FlexChild>
                            <div align="right">
                                <Logo
                                    shortName={data.model.originator.shortName}
                                    height={theme.grid.originatorLogoWidth}
                                    width={theme.grid.originatorLogoWidth}
                                />
                            </div>
                        </FlexChild>
                        <FlexChild>
                            <div align="right">
                                <ModelLabel modelName={data.model.name}/>
                            </div>
                        </FlexChild>
                    </Flex>
                </FlexChild>
            </Flex>
            <table style={{tableLayout: "fixed", width: "100%"}}>
                <tbody>
                <tr>
                    <td style={{fontWeight: "bold", width: "90px"}}>
                        Model status:
                    </td>
                    <td>
                        <Status
                            status={data.status}
                            validFromDate={data.validFromDate}
                            validToDate={data.validToDate}
                        />
                    </td>
                </tr>
                <tr>
                    <td style={{fontWeight: "bold", width: "90px"}}>
                        Processed:
                    </td>
                    <td>
                        <Enabled
                            enabled={data.enabled}
                        />
                    </td>
                </tr>
                <tr>
                    <td colSpan={2} style={{paddingTop: "10px", fontWeight: "bold"}}>Data Format</td>
                </tr>
                <tr>
                    <td style={{verticalAlign: "top"}}>
                        Originator:
                    </td>
                    <td>
                        {
                            data.dataFormat.originator.map(
                                x => x.grid+" ("+x.format.join(", ")+")"
                            ).join(", ")
                        }
                    </td>
                </tr>
                <tr>
                    <td style={{verticalAlign: "top"}}>
                        DTN:
                    </td>
                    <td>
                        {
                            data.dataFormat.mg
                                .filter(x => !x.legacy)
                                .map(x => `${x.grid} (${x.format.join(", ")})`)
                                .join(", ")
                        }
                    </td>
                </tr>
                <tr>
                    <td style={{paddingTop: "10px", fontWeight: "bold"}}>
                        Spatial Range:
                    </td>
                    <td style={{paddingTop: "10px"}}>
                        {data.spatialRange.description}
                    </td>
                </tr>
                {data.accumulation_duration && <tr>
                    <td style={{paddingTop: "10px", fontWeight: "bold"}}>
                    Duration:
                    </td>
                    <td style={{paddingTop: "10px"}}>
                        {data.accumulation_duration}
                    </td>
                </tr>}
                <tr>
                    <td colSpan={2} style={{paddingTop: "10px", fontWeight: "bold"}}>Updates/Time-Range</td>
                </tr>
                <tr>
                    <td style={{verticalAlign: "top"}} colSpan={2}>
                        <UpdateFrequencyList updateFrequency={data.updateFrequency}/>
                    </td>
                </tr>
                </tbody>
            </table>

            <Flex>
                <FlexChild>
                    <Button onClick={() => this.modelSearch.searchAndShow(data)}>
                        <FontAwesomeIcon icon={"globe"}/>
                        Model
                    </Button>
                </FlexChild>
                <FlexChild>
                    <Button onClick={() => this.parameterSearch.searchAndShow(data)}>
                        <FontAwesomeIcon icon={"cloud-sun-rain"}/>
                        Parameter
                    </Button>
                </FlexChild>
                <FlexChild>
                    <Button onClick={() => this.gridSearch.searchAndShow(data)}>
                        <FontAwesomeIcon icon={"drafting-compass"}/>
                        Grid
                    </Button>
                </FlexChild>
                <FlexChild>
                    <Button onClick={() => this.accessSearch.searchAndShow(data)}>
                        <FontAwesomeIcon icon={"key"}/>
                        Access
                    </Button>
                </FlexChild>
            </Flex>
        </ResultItem>
        );
    };

    onError = (error, handleError) => {
        if (error instanceof Error) {
            // This is an Error object, which we can handle normally
            let errorMessage;
            const errorJson = tryParseJSON(error.message);
            if (errorJson === false) {
                errorMessage = error.message;
            } else {
                errorMessage = errorJson.Message || "";
            }
            handleError(500, errorMessage);
        } else {
            // This is, potentially, a promise on a Response, and it is already called.
            // We need to clone the Response to call it again.
            const errorClone = error.clone();

            if (errorClone.text && typeof errorClone.text === "function") {
                errorClone.text().then((text) => {
                    const errorCode = error.status;
                    let errorMessage;
                    const errorBody = text;
                    const errorJson = tryParseJSON(errorBody);
                    if (errorJson === false) {
                        errorMessage = errorBody;
                    } else {
                        errorMessage = errorJson.Message || "";
                    }
                    if (errorJson.error && errorJson.error.reason) {
                        errorMessage += " " + errorJson.error.reason;
                    }
                    handleError(errorCode, errorMessage, errorBody);
                });
            } else {
                // What else can it be?
                // Let's hope this has a nice str representation.
                handleError(500, "Unknown error", error);
            }
        }
    };

    render = () => (
        <ReactiveList
            className={resultListContainer}
            componentId="results"
            dataField="sortKey"
            innerClass={{
                list: 'result-list-container',
                pagination: 'result-list-pagination',
                resultsInfo: 'result-list-info',
                poweredBy: 'powered-by',
            }}
            onData={(data) => {
                console.log('Loading item:', data.name);
                return this.onData(data);
            }}
            onError={error => this.onError(error, this.props.handleError)}
            onNoResults={<NoResult/>}
            onResultStats={onResultStats}
            onPageChange={(page) => {
                console.log('Page changed to:', page);
            }}
            onQueryChange={(prevQuery, nextQuery) => {
                console.log('Query changed - from:',
                    prevQuery && prevQuery.from,
                    'to:',
                    nextQuery && nextQuery.from
                );
            }}
            react={{
                and: ['model', 'levelType', 'textSearch', 'derived', 'enabled'],
            }}
            pagination={true}
            paginationAt={"both"}
            size={12}
            sortOptions={[
                {
                    label: 'Parameter asc',
                    dataField: 'sortKey.keyword',
                    sortBy: 'asc',
                },
                {
                    label: 'Parameter desc',
                    dataField: 'sortKey.keyword',
                    sortBy: 'desc',
                },
            ]}
        />
    );
}

export default Results;



// WEBPACK FOOTER //
// ./src/components/results/Results.js